<script setup>
defineOptions({ name: "CareersPage" });
import { ref } from "vue";
import { useSeo } from "@/composables/useSeo";
import { ChevronDownIcon } from "@heroicons/vue/24/outline";
import FaqPanel from "@/components/ui/FAQ.vue";

useSeo({
  title: "Carreiras | Kali Sistemas",
  description:
    "Faça parte do time da Kali Sistemas. Construa sua carreira com propósito, inovação e colaboração.",
});

// Portal de vagas (app separado em vagas/, deploy em vagas.kalisistemas.com.br)
const vagasUrl = import.meta.env?.VITE_VAGAS_URL || "https://vagas.kalisistemas.com.br";

const teams = [
  {
    name: "Tecnologia e Engenharia",
    description:
      "Construa sistemas robustos e inovadores que transformam negócios.",
    image:
      "/src/assets/equipengtec.webp",
  },
  {
    name: "Vendas e Comercial",
    description: "Conecte nossas soluções às necessidades reais dos clientes.",
    image:
      "/src/assets/equipcomercial.webp",
  },
  {
    name: "Suporte e Atendimento",
    description:
      "Seja a voz e a ajuda essencial para o sucesso dos nossos usuários.",
    image:
      "/src/assets/equipsuporte.webp",
  },
  {
    name: "Administrativo e Operações",
    description:
      "A base sólida que mantém a Kali Sistemas crescendo de forma estruturada.",
    image:
      "/src/assets/equipadmin.webp",
  },
];

const benefits = [
  {
    title: "Saúde e Bem-estar",
    description:
      "Plano de saúde, plano odontológico e auxílio bem-estar para você e sua família.",
  },
  {
    title: "Flexibilidade",
    description:
      "Modelos de trabalho híbrido ou remoto, adaptados para o seu melhor rendimento.",
  },
  {
    title: "Desenvolvimento",
    description:
      "Verba para cursos, acesso à plataformas de aprendizado e plano de carreira.",
  },
  {
    title: "Reconhecimento",
    description:
      "Participação nos resultados (PLR) e premiações por entregas excepcionais.",
  },
];

const processSteps = [
  {
    title: "Cadastro",
    description: "Preencha o formulário no portal de vagas com seus dados e currículo.",
  },
  {
    title: "Contato",
    description: "Se o seu perfil combinar com a vaga, entramos em contato por e-mail ou telefone.",
  },
  {
    title: "Seleção",
    description: "Entrevistas e, dependendo da vaga, testes para conhecer melhor seu perfil.",
  },
  {
    title: "Retorno",
    description: "Damos um retorno sobre a sua participação, independente do resultado.",
  },
  {
    title: "Aprovação",
    description: "Aprovado(a)? Seguimos com os trâmites finais de contratação.",
  },
];

const interviewTips = [
  {
    title: "Checklist para nosso processo on-line",
    items: [
      "Boa conexão com a internet",
      "Tenha o link e o horário da entrevista em mãos",
      "Configure seu microfone/fone/headset com antecedência",
      "Preferimos ver você, por isso, disponha de uma câmera/webcam (também pode ser feito via celular)",
    ],
  },
  {
    title: "Confira nossas dicas para se preparar",
    items: [
      "Preencha seus dados com atenção no cadastro e seja transparente sobre suas experiências",
      "Fale sobre você! Isso é bem relevante para nós",
      "Fique de olho no seu WhatsApp e e-mail, o contato pode ser feito por lá",
      "A entrevista é um bate-papo tranquilo: tenha em mente seus principais conhecimentos e experiências",
    ],
  },
];

const openTip = ref(0);
const toggleTip = (idx) => {
  openTip.value = openTip.value === idx ? null : idx;
};

const faqItems = [
  {
    question: "Quanto tempo dura o processo seletivo?",
    answer:
      "Varia conforme a vaga e a área, mas buscamos manter cada etapa ágil e dar retorno o quanto antes.",
  },
  {
    question: "Por quais meios vou ser contatado?",
    answer:
      "Normalmente por e-mail ou telefone/WhatsApp, usando os dados informados na sua candidatura.",
  },
  {
    question: "Quanto tempo demora para eu receber retorno depois da entrevista?",
    answer: "Fazemos o possível para retornar em poucos dias úteis após cada etapa.",
  },
  {
    question: "Não fui chamado para a entrevista, o que aconteceu?",
    answer:
      "Analisamos todos os perfis recebidos, mas nem sempre é possível chamar todos os candidatos para as próximas etapas.",
  },
  {
    question: "Fiz a entrevista e não fui aprovado, o que acontece com meu currículo?",
    answer:
      "Seu currículo fica no nosso banco de talentos e pode ser considerado para futuras oportunidades.",
  },
  {
    question: "Posso me candidatar a mais de uma vaga?",
    answer: "Sim! Você pode se candidatar a quantas vagas fizer sentido para o seu perfil.",
  },
];
</script>

<template>
  <div class="min-h-screen bg-white font-sans">
    <!-- Hero Section similar to Thomson Reuters -->
    <div class="relative bg-slate-900 overflow-hidden">
      <!-- Background Image with Overlay -->
      <div class="absolute inset-0">
        <img
          src="@/assets/hero-careers.webp"
          alt="Equipe trabalhando"
          class="w-full h-full object-cover opacity-40"
        />
        <div
          class="absolute inset-0 bg-gradient-to-r from-slate-900 to-transparent"
        ></div>
      </div>

      <div
        class="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 lg:py-48"
      >
        <div class="max-w-2xl">
          <h1
            class="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-tight text-white text-shadow-hero mb-6"
          >
            Construa uma carreira com propósito
          </h1>
          <p class="text-xl text-slate-200 mb-10 leading-relaxed">
            Junte-se à Kali Sistemas e faça parte de um time que cria
            tecnologias para simplificar a gestão de milhares de empresas.
          </p>
        </div>
      </div>
    </div>

    <!-- Intro / Purpose Section -->
    <section class="py-20 bg-white">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="max-w-3xl mx-auto text-center">
          <h2
            class="text-4xl sm:text-5xl font-black tracking-tight !leading-[1.25] pb-2 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b] mb-4"
          >
            Trabalhe conosco e faça a diferença
          </h2>
          <p class="text-lg text-gray-600 leading-relaxed">
            Na Kali Sistemas, acreditamos que a tecnologia tem o poder de
            transformar a maneira como as pessoas trabalham. Procuramos mentes
            curiosas, pessoas apaixonadas por resolver problemas e que queiram
            crescer em um ambiente inclusivo, inovador e focado no sucesso do
            cliente.
          </p>
        </div>
      </div>
    </section>

    <!-- Teams / Areas Section -->
    <section id="equipes" class="py-20 bg-slate-50 border-t border-slate-100 scroll-mt-20">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="mb-12">
          <h2
            class="text-4xl sm:text-5xl font-black tracking-tight !leading-[1.25] pb-2 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b] mb-2"
          >
            Nossas Equipes
          </h2>
          <p class="text-lg text-gray-600">
            Encontre a área onde seu talento brilhará mais.
          </p>
        </div>

        <div class="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          <div
            v-for="team in teams"
            :key="team.name"
            class="group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-shadow duration-300 border border-gray-100 cursor-pointer"
          >
            <div class="aspect-16/10 w-full overflow-hidden">
              <img
                :src="team.image"
                :alt="team.name"
                class="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div class="p-6">
              <h3
                class="text-xl font-bold text-[#006086] mb-2 group-hover:text-[#004d6b] transition-colors"
              >
                {{ team.name }}
              </h3>
              <p class="text-gray-600 text-sm leading-relaxed">
                {{ team.description }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Culture Section (Split layout) -->
    <section class="py-20 bg-white">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="lg:grid lg:grid-cols-2 lg:gap-16 items-center">
          <div class="mb-12 lg:mb-0">
            <h2
              class="text-4xl sm:text-5xl font-black tracking-tight !leading-[1.25] pb-2 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b] mb-4"
            >
              Nossa Cultura e Valores
            </h2>
            <p class="text-lg text-gray-600 mb-6 leading-relaxed">
              Valorizamos a diversidade de ideias e a autonomia com
              responsabilidade. Aqui, a colaboração não é apenas uma palavra da
              moda — é como construímos soluções que realmente importam.
            </p>
            <ul class="space-y-4">
              <li class="flex items-start">
                <div class="flex-shrink-0 mt-1">
                  <div class="w-2 h-2 rounded-full bg-[#006086]"></div>
                </div>
                <p class="ml-4 text-gray-700">
                  <strong>Transparência total:</strong> Comunicação aberta e
                  direta em todos os níveis.
                </p>
              </li>
              <li class="flex items-start">
                <div class="flex-shrink-0 mt-1">
                  <div class="w-2 h-2 rounded-full bg-[#006086]"></div>
                </div>
                <p class="ml-4 text-gray-700">
                  <strong>Senso de dono:</strong> Todos têm voz e
                  responsabilidade pelo impacto que geram.
                </p>
              </li>
              <li class="flex items-start">
                <div class="flex-shrink-0 mt-1">
                  <div class="w-2 h-2 rounded-full bg-[#006086]"></div>
                </div>
                <p class="ml-4 text-gray-700">
                  <strong>Inovação constante:</strong> Incentivamos testes,
                  aprendizados e novas abordagens.
                </p>
              </li>
            </ul>
            <div class="mt-8">
              <a
                :href="vagasUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="text-[#006086] font-bold hover:underline inline-flex items-center"
              >
                Veja as vagas abertas
                <svg
                  class="w-4 h-4 ml-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 5l7 7-7 7"
                  ></path>
                </svg>
              </a>
            </div>
          </div>
          <div class="relative">
            <img
              src="@/assets/carrers-culturaevalor.webp"
              alt="Cultura Kali Sistemas"
              class="rounded-xl shadow-2xl"
            />
            <div
              class="absolute -bottom-6 -left-6 bg-kali-gold p-6 rounded-lg shadow-lg max-w-xs hidden sm:block"
              style="background-color: #e5be35"
            >
              <p class="text-gray-900 font-bold text-lg">
                "O melhor lugar para desenvolver todo o seu potencial."
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Benefits Section -->
    <section class="py-20 bg-slate-900 text-white">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-16">
          <h2 class="text-4xl sm:text-5xl font-black tracking-tight leading-tight mb-4">
            Benefícios que acompanham você
          </h2>
          <p class="text-slate-300 text-lg max-w-2xl mx-auto">
            Pensamos em cada detalhe para que você tenha a tranquilidade
            necessária para dar o seu melhor.
          </p>
        </div>

        <div class="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          <div
            v-for="benefit in benefits"
            :key="benefit.title"
            class="bg-slate-800 p-6 rounded-xl border border-slate-700"
          >
            <div
              class="w-12 h-12 bg-[#006086]/20 rounded-lg flex items-center justify-center mb-6"
            >
              <div class="w-4 h-4 rounded-full bg-[#006086]"></div>
            </div>
            <h3 class="text-xl font-bold mb-3">{{ benefit.title }}</h3>
            <p class="text-slate-400 text-sm leading-relaxed">
              {{ benefit.description }}
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Fases do processo -->
    <section class="py-20 bg-white">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="max-w-2xl mx-auto text-center mb-12">
          <h2
            class="text-4xl sm:text-5xl font-black tracking-tight !leading-[1.25] pb-2 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b] mb-4"
          >
            Fases do nosso processo
          </h2>
          <p class="text-lg text-gray-600">
            Um processo seletivo online, ágil e transparente do início ao fim.
          </p>
        </div>

        <div class="grid gap-6 sm:grid-cols-2 lg:grid-cols-5">
          <div
            v-for="(step, idx) in processSteps"
            :key="step.title"
            class="p-6 bg-slate-50 border border-slate-100 rounded-2xl"
          >
            <div
              class="inline-flex items-center justify-center w-10 h-10 font-bold text-white rounded-xl bg-[#006086]"
            >
              {{ idx + 1 }}
            </div>
            <h3 class="mt-4 text-lg font-bold text-gray-900">{{ step.title }}</h3>
            <p class="mt-2 text-sm leading-relaxed text-gray-600">
              {{ step.description }}
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Vagas para PcDs -->
    <section class="py-20 bg-slate-50 border-t border-slate-100">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="lg:grid lg:grid-cols-2 lg:gap-16 items-start">
          <div class="mb-12 lg:mb-0">
            <img
              src="@/assets/pcds.webp"
              alt="Colaborador trabalhando"
              class="rounded-xl shadow-2xl"
            />
          </div>

          <div>
            <h2
              class="text-4xl sm:text-5xl font-black tracking-tight !leading-[1.25] pb-2 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b] mb-4"
            >
              Vagas para PCDs
            </h2>
            <p class="text-lg text-gray-600 mb-6 leading-relaxed">
              Acreditamos no pontecial único de cada pessoa. Aqui, todas as
              vozes importam, todas as ideias transformam e todos têm
              espaço para crescer.
            </p>
            <p class="text-lg text-gray-600 mb-6 leading-relaxed">
              Vagas para PCDs com oportunidade reais de desenvolvimento e
              inclusão.
            </p>
            <a
              :href="vagasUrl"
              class="inline-flex justify-center items-center px-6 py-3 text-base font-bold text-white rounded-full shadow-sm transition-colors bg-[#006086] hover:bg-[#004d6b]"
            >
              Consulte todas as vagas
            </a>
          </div>
        </div>
      </div>
    </section>

    <!-- Dicas para entrevista -->
    <section class="py-20 bg-white">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2
          class="text-4xl sm:text-5xl font-black tracking-tight !leading-[1.25] pb-2 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b] mb-12"
        >
          Dicas para mandar bem na entrevista
        </h2>

        <div class="grid gap-10 sm:grid-cols-2">
          <div v-for="(tip, idx) in interviewTips" :key="tip.title">
            <button
              type="button"
              class="flex justify-between items-center pb-4 w-full text-left border-b border-gray-300"
              @click="toggleTip(idx)"
            >
              <span class="text-lg font-bold text-[#006086]">{{ tip.title }}</span>
              <ChevronDownIcon
                class="flex-shrink-0 ml-4 w-5 h-5 text-[#006086] transition-transform"
                :class="{ 'rotate-180': openTip === idx }"
              />
            </button>
            <ul v-if="openTip === idx" class="pt-6 space-y-3">
              <li v-for="item in tip.items" :key="item" class="flex gap-2 text-gray-700">
                <span class="text-[#006086]">-</span>
                <span>{{ item }}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- Dúvidas frequentes -->
    <FaqPanel
      title="Dúvidas frequentes"
      :items="faqItems"
      description="Tire suas dúvidas sobre o processo seletivo da Kali Sistemas."
      cta-label="Ver vagas abertas"
      :cta-href="vagasUrl"
      section-class="bg-slate-50"
    />

    <!-- Chamada final -->
    <section id="candidatura" class="overflow-hidden relative bg-gradient-to-r from-[#006086] to-[#004d6b]">
      <div class="absolute inset-0">
        <svg
          class="absolute inset-0 w-full h-full"
          preserveAspectRatio="xMidYMid slice"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 1463 360"
        >
          <path
            class="text-[#004d6b] text-opacity-40"
            fill="currentColor"
            d="M-82.673 72l1761.849 472.086-134.327 501.315-1761.85-472.086z"
          />
          <path
            class="text-[#003d56] text-opacity-40"
            fill="currentColor"
            d="M-217.088 544.086L1544.761 72l134.327 501.316-1761.849 472.086z"
          />
        </svg>
      </div>

      <div class="relative px-4 py-16 mx-auto max-w-7xl sm:py-24 sm:px-6 lg:px-8">
        <div class="grid gap-8 lg:grid-cols-2 lg:items-center">
          <div>
            <h2 class="text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
              <span class="block">Pronto para dar o</span>
              <span class="block text-transparent bg-clip-text bg-gradient-to-r from-[#e6f0f4] to-[#007ba8]">
                próximo passo?
              </span>
            </h2>
            <p class="mt-4 text-lg text-[#e6f0f4] max-w-3xl">
              Veja as vagas abertas no nosso portal — quando abrir uma vaga com
              o seu perfil, você poderá se candidatar diretamente por lá.
            </p>
          </div>
          <div class="flex flex-col gap-3 sm:flex-row lg:justify-end">
            <a
              :href="vagasUrl"
              class="inline-flex justify-center items-center px-6 py-4 text-xl font-medium text-[#006086] bg-white rounded-xl border-2 border-white shadow-xl transition-colors hover:bg-[#e6f0f4]"
            >
              Ver todas as vagas abertas
            </a>
            <a
              href="#equipes"
              class="inline-flex justify-center items-center px-6 py-4 text-xl font-medium text-white rounded-xl border-2 border-[#007ba8] transition-colors hover:bg-[#004d6b]"
            >
              Ver nossas equipes
            </a>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped>
.text-shadow-hero {
  text-shadow:
    0 2px 10px rgba(0, 0, 0, 0.65),
    0 1px 2px rgba(0, 0, 0, 0.55);
}
</style>
